import { formatRateRange } from 'pages/pricelists/rateRange';
import { JobRoleGroupType } from 'types/jobRoles';
import { EmployeeRate, HourlyRate } from 'types/pricelists';

type RatesActions = {
    hourlyRateSummary: string;
    hourlyRateDetails: Record<string, HourlyRate[]>;
};

export interface StoredRateSummary {
    minHourlyPrice?: string | number | null;
    maxHourlyPrice?: string | number | null;
    avgHourlyPrice?: string | number | null;
}

const pricedRates = (rates: HourlyRate[] | undefined): HourlyRate[] =>
    (rates || []).filter((item) => item.hourlyPrice !== null && item.hourlyPrice !== undefined);

/**
 * The priced rows grouped by job role group, each group from the highest price down and ties in role order;
 * the tooltip breakdown and the clipboard copy. Roles excluded from the min/max are kept.
 */
export const groupHourlyRates = (rates: HourlyRate[] | undefined): Record<string, HourlyRate[]> =>
    [...pricedRates(rates)]
        .sort((a, b) => {
            const priceA = Number(a.hourlyPrice ?? Infinity);
            const priceB = Number(b.hourlyPrice ?? Infinity);
            const priceDiff = priceB - priceA;

            if (priceDiff !== 0) {
                return priceDiff;
            }

            return (a.jobRole.orderNum ?? Infinity) - (b.jobRole.orderNum ?? Infinity);
        })
        .reduce((groups: Record<string, HourlyRate[]>, rate: HourlyRate) => {
            const group = rate.jobRole.groupNameTypeCode ?? JobRoleGroupType.LEGAL;
            (groups[group] || (groups[group] = [])).push(rate);
            return groups;
        }, {});

/**
 * The tooltip breakdown of a rate list and its one-line summary. The summary prefers the stored per-list
 * figures — min, max and avg, personal rows included — and only falls back to a browser-side min/max for a
 * list that has not been saved yet and so has no stored figures: over the role rows, or over the people of an
 * employee-priced list, where every priced person counts.
 */
export const useHourlyRates = (
    rates: HourlyRate[] | undefined,
    stored?: StoredRateSummary,
    employeeRates?: EmployeeRate[]
): RatesActions => {
    const allValidRates = pricedRates(rates);

    const activeRates = allValidRates.filter((item) => !item.jobRole.excludeFromMinmaxCalculation);

    const hourlyPrices: number[] = employeeRates
        ? employeeRates
              .filter((rate) => rate.hourlyPrice !== null && rate.hourlyPrice !== '')
              .map((rate) => Number(rate.hourlyPrice))
        : activeRates.map((item) => Number(item.hourlyPrice));
    const computedSummary: string = hourlyPrices.length
        ? `${Math.min(...hourlyPrices)}–${Math.max(...hourlyPrices)}`
        : '';
    const hourlyRateSummary =
        stored?.minHourlyPrice != null && stored?.maxHourlyPrice != null
            ? formatRateRange(stored.minHourlyPrice, stored.maxHourlyPrice, stored.avgHourlyPrice)
            : computedSummary;

    const hourlyRateDetails = groupHourlyRates(rates);

    return {
        hourlyRateSummary,
        hourlyRateDetails,
    };
};
